import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Chip, LinearProgress, Paper, Button, List, ListItem, ListItemText } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ensureLogin, fetchProjectById, fetchScheduleTasks, fetchProjectActivities } from '../utils/api';
import { formatCurrency, formatDate as formatDateUtil } from '../utils/format';

const ProjectDashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      setErrorMessage('');
      try {
        await ensureLogin();
        let p = null, t = [], a = [];
        try { p = await fetchProjectById(Number(id)); } catch (e) { setErrorMessage('Falha ao carregar o projeto.'); }
        try { t = await fetchScheduleTasks(Number(id)); } catch (e) { setErrorMessage((prev) => prev ? prev + ' Falha ao carregar tarefas.' : 'Falha ao carregar tarefas.'); }
        try { a = await fetchProjectActivities(Number(id)); } catch (e) { /* ignora */ }
        if (p) setProject(p);
        setTasks(Array.isArray(t) ? t.map((task) => ({
          ...task,
          budget: (task.budget !== undefined && task.budget !== null) ? Number(task.budget) : 0,
          outputs: (() => { try { const arr = JSON.parse(task.outputs || '[]'); return Array.isArray(arr) ? arr : []; } catch(e) { return []; } })()
        })) : []);
        setActivities(Array.isArray(a) ? a : []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const getStatusText = (status) => {
    switch (status) {
      case 'planning': return 'Planeamento';
      case 'kickoff': return 'Kick-off';
      case 'execution': return 'Execução';
      case 'closing': return 'Encerramento';
      case 'completed': return 'Concluído';
      default: return 'Desconhecido';
    }
  };

  const overallProgress = () => {
    if (!tasks.length) return 0;
    const completed = tasks.filter(t => t.status === 'concluido').length;
    return Math.round((completed / tasks.length) * 100);
  };

  const upcomingTasks = () => {
    const now = new Date();
    const soon = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return tasks
      .filter(t => {
        const end = t.endDate ? new Date(t.endDate) : null;
        const active = t.status !== 'concluido' && t.status !== 'cancelado';
        return end && end >= now && end <= soon && active;
      })
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
      .slice(0, 5);
  };

  const outputsSummary = () => {
    const totals = { count: 0, concluidos: 0, pendentes: 0, previsto: 0, real: 0 };
    (Array.isArray(tasks) ? tasks : []).forEach((t) => {
      const outs = Array.isArray(t.outputs) ? t.outputs : [];
      totals.count += outs.length;
      totals.concluidos += outs.filter((o) => (o.status || 'pendente') === 'concluido').length;
      totals.pendentes += outs.filter((o) => (o.status || 'pendente') !== 'concluido').length;
      totals.previsto += outs.reduce((sum, o) => sum + (parseFloat(o.plannedValue) || 0), 0);
      totals.real += outs.reduce((sum, o) => sum + (parseFloat(o.actualValue) || 0), 0);
    });
    return totals;
  };

  const overdueTasks = () => {
    const now = new Date();
    return tasks
      .filter(t => {
        const end = t.endDate ? new Date(t.endDate) : null;
        const active = t.status !== 'concluido' && t.status !== 'cancelado';
        return end && end < now && active;
      })
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate))
      .slice(0, 5);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Dashboard do Projeto</Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/projects/${id}`)}>Voltar ao Projeto</Button>
      </Box>

      {!!errorMessage && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #f5c2c7', bgcolor: '#f8d7da', color: '#842029', borderRadius: 1 }}>
          <Typography>{errorMessage}</Typography>
        </Box>
      )}

      {loading && <Typography>A carregar…</Typography>}

      {!loading && project && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{project.title}</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip label={getStatusText(project.status)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Período</Typography>
                    <Typography>{formatDateUtil(project.startDate)} → {formatDateUtil(project.endDate)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Orçamento</Typography>
                    <Typography>{formatCurrency(project.budget || 0)}</Typography>
                  </Grid>
                </Grid>
                {(() => {
                  const s = outputsSummary();
                  const budget = Number(project?.budget || 0);
                  if (budget <= 0) return null;
                  const totalTaskBudgets = (Array.isArray(tasks) ? tasks : []).reduce((sum, t) => sum + (Number(t.budget) || 0), 0);
                  const devPrev = budget - s.previsto;
                  const devReal = budget - s.real;
                  const pctReal = s.real > 0 ? Math.round((s.real / budget) * 100) : 0;
                  return (
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`Budget tarefas: ${formatCurrency(totalTaskBudgets)}`} color={totalTaskBudgets > budget ? 'error' : 'default'} />
                      <Chip label={`Δ Budget tarefas: ${formatCurrency(budget - totalTaskBudgets)}`} color={(budget - totalTaskBudgets) < 0 ? 'error' : ((budget - totalTaskBudgets) < budget * 0.2 ? 'warning' : 'default')} />
                      <Chip label={`Desvio Previsto: ${formatCurrency(devPrev)}`} color={devPrev < 0 ? 'error' : (devPrev < budget * 0.2 ? 'warning' : 'default')} />
                      <Chip label={`Desvio Real: ${formatCurrency(devReal)}`} color={devReal < 0 ? 'error' : 'default'} />
                      <Chip label={`Consumo real: ${pctReal}%`} color={pctReal >= 80 ? 'warning' : 'default'} />
                    </Box>
                  );
                })()}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">Progresso geral (média das tarefas)</Typography>
                  <LinearProgress variant="determinate" value={overallProgress()} sx={{ mt: 1 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Tarefas em atraso</Typography>
                {overdueTasks().length === 0 ? (
                  <Typography color="text.secondary">Nenhuma tarefa em atraso.</Typography>
                ) : (
                  <List>
                    {overdueTasks().map((t) => (
                      <ListItem key={`overdue-${t.id}`} divider>
                        <ListItemText primary={t.name} secondary={`Fim: ${formatDateUtil(t.endDate)} • Resp.: ${t.responsible || '—'}`} />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Resumo de Outputs</Typography>
                {(() => {
                  const s = outputsSummary();
                  return (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`Total: ${s.count}`} />
                      <Chip label={`Concluídos: ${s.concluidos}`} color="success" />
                      <Chip label={`A concluir: ${s.pendentes}`} color="warning" />
                      <Chip label={`Previsto: ${formatCurrency(s.previsto)}`} color="primary" />
                      <Chip label={`Real: ${formatCurrency(s.real)}`} color="secondary" />
                      {Number(project?.budget || 0) > 0 && (
                        <Chip label={`Δ Previsto: ${formatCurrency(Number(project.budget) - s.previsto)}`} />
                      )}
                    </Box>
                  );
                })()}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Outputs são geridos na aba Execução do projeto.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Próximas tarefas (7 dias)</Typography>
              {upcomingTasks().length === 0 ? (
                <Typography color="text.secondary">Sem tarefas próximas.</Typography>
              ) : (
                <List>
                  {upcomingTasks().map((t) => (
                    <ListItem key={`upcoming-${t.id}`} divider>
                      <ListItemText primary={t.name} secondary={`Fim: ${formatDateUtil(t.endDate)} • Resp.: ${t.responsible || '—'}`} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Atividades Globais</Typography>
              {activities.length === 0 ? (
                <Typography color="text.secondary">Sem atividades globais definidas.</Typography>
              ) : (
                <List>
                  {activities.map((a) => (
                    <ListItem key={`activity-${a.id}`} divider>
                      <ListItemText primary={a.title} secondary={`Duração: ${a.durationText || '—'}`} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default ProjectDashboard;
